import { createStore } from 'vuex'
import Cookies from 'js-cookie'
import axios from 'axios'

import {
    USERS_PATH,
    MEMBERS_PATH,
    WWW_MEMBERS_PATH,
    COOKIE_SECURE,
    COOKIE_LOCALE,
    COOKIE_LOCALE_VALIDITY_S,
    COOKIE_ACCESS_TOKEN_MEMBER,
    COOKIE_ACCESS_TOKEN_MEMBER_VALIDITY_S,
    COOKIE_REFRESH_TOKEN_MEMBER,
    COOKIE_REFRESH_TOKEN_MEMBER_VALIDITY_S,
    COOKIE_CONSENT_VALIDITY_S,
    DOMAIN
} from '@/common/constants'

// import SerializeQuery from '@/common/mixin/SerializeQuery'

export default function make_store (EventBus) {
    let refresh_request = null
    window.plausible = window.plausible || function () {
        (window.plausible.q = window.plausible.q || []).push(arguments)
    }

    const store = createStore({
        state () {
            return {
                member: {},
                location: {},
                access_token_member: '',
                refresh_token_member: '',
                is_authorized_member: false,
                locale: Cookies.get(COOKIE_LOCALE) || 'en'
            }
        },
        mutations: {
            TRACK_GOAL (state, value) {
                window.plausible(value.goal)
            },
            SET_LOCALE (state, value) {
                state.locale = value
                Cookies.set(COOKIE_LOCALE, value, {
                    expires: COOKIE_LOCALE_VALIDITY_S / 3600 / 24,
                    secure: COOKIE_SECURE,
                    domain: DOMAIN,
                    path: '/',
                    sameSite: 'strict'
                })
            },
            SET_COOKIE_CONSENT (state, value) {
                Cookies.set(value.cookie_name, value.cookie_value, {
                    expires: COOKIE_CONSENT_VALIDITY_S / 3600 / 24,
                    secure: COOKIE_SECURE,
                    domain: DOMAIN,
                    path: '/',
                    sameSite: 'strict'
                })
            },
            UNSET_MEMBER (state, value) {
                state.member = {}
            },
            UNSET_TOKEN_MEMBER (state, value) {
                state.access_token_member = ''
                state.refresh_token_member = ''
                state.is_authorized_member = false
                Cookies.remove(COOKIE_ACCESS_TOKEN_MEMBER, { path: '/', domain: DOMAIN })
                Cookies.remove(COOKIE_REFRESH_TOKEN_MEMBER, { path: '/', domain: DOMAIN })
            },
            RESTORE_TOKEN_MEMBER (state, value) {
                state.access_token_member = typeof value.access_token === 'string' ? value.access_token : ''
                state.refresh_token_member = typeof value.refresh_token === 'string' ? value.refresh_token : ''
                state.is_authorized_member = state.access_token_member.split('.').length === 3
            },
            INIT_TOKEN_MEMBER (state, value) {
                state.access_token_member = value.access_token
                state.refresh_token_member = value.refresh_token
                state.is_authorized_member = true
                Cookies.set(COOKIE_ACCESS_TOKEN_MEMBER, value.access_token, {
                    expires: COOKIE_ACCESS_TOKEN_MEMBER_VALIDITY_S / 3600 / 24,
                    secure: COOKIE_SECURE,
                    domain: DOMAIN,
                    path: '/',
                    sameSite: 'strict'
                })
                Cookies.set(COOKIE_REFRESH_TOKEN_MEMBER, value.refresh_token, {
                    expires: COOKIE_REFRESH_TOKEN_MEMBER_VALIDITY_S / 3600 / 24,
                    secure: COOKIE_SECURE,
                    domain: DOMAIN,
                    path: '/',
                    sameSite: 'strict'
                })
            },
            SET_MEMBER (state, value) {
                state.member = value.member
            }
        },
        getters: {
        },
        actions: {
            async GET_PUSH_CONFIG () {
                try {
                    return await axios.get(`${MEMBERS_PATH}/push/delivery/config`)
                } catch (error) {
                    return error.response
                }
            },
            async PUSH_SUBSCRIBE (context, data) {
                try {
                    return await axios.post(`${MEMBERS_PATH}/subscribe/delivery`, data)
                } catch (error) {
                    return error.response
                }
            },
            async PUSH_UNSUBSCRIBE (context, data) {
                try {
                    return await axios.delete(`${MEMBERS_PATH}/subscribe/delivery`, {
                        data: { endpoint: data.endpoint }
                    })
                } catch (error) {
                    return error.response
                }
            },
            async REMOVE_BROWSER_PUSH_SUBSCRIPTION (context) {
                if (!('serviceWorker' in navigator) || !('PushManager' in window)) return

                try {
                    const registration = await navigator.serviceWorker.getRegistration()
                    if (!registration || !registration.pushManager) return

                    const subscription = await registration.pushManager.getSubscription()
                    if (!subscription) return

                    if (typeof subscription.endpoint === 'string' && subscription.endpoint.length > 0) {
                        await context.dispatch('PUSH_UNSUBSCRIBE', { endpoint: subscription.endpoint })
                    }
                    await subscription.unsubscribe()
                } catch (error) {
                    // Logging out must still succeed when browser push cleanup is unavailable.
                }
            },
            async LOGIN_MEMBER (context, data) {
                const headers = {}
                let response = {}
                try {
                    await context.dispatch('REMOVE_BROWSER_PUSH_SUBSCRIPTION')
                    response = await axios.post(`${WWW_MEMBERS_PATH}/login`, { ...data }, { headers })
                    context.commit('INIT_TOKEN_MEMBER', response.data)
                    await context.dispatch('GET_MEMBER_INFO', 'TOKEN_MEMBER')
                } catch (error) {
                    return error.response
                }
                return response
            },
            async LOGOUT_MEMBER (context) {
                const headers = {
                    'x-token': context.state.access_token_member
                }
                let response = {}
                try {
                    await context.dispatch('REMOVE_BROWSER_PUSH_SUBSCRIPTION')
                    response = await axios.post(`${MEMBERS_PATH}/logout`, {}, { headers })
                    context.commit('UNSET_MEMBER')
                    context.commit('UNSET_TOKEN_MEMBER')
                } catch (error) {
                    context.commit('UNSET_MEMBER')
                    context.commit('UNSET_TOKEN_MEMBER')
                    return error.response
                }
                return response.data
            },
            async GET_MEMBER_INFO (context) {
                if (!context.state.access_token_member) {
                    return { status: 8 }
                }
                const headers = {
                    'x-token': context.state.access_token_member
                }
                let response = {}
                try {
                    response = await axios.get(`${MEMBERS_PATH}/info`, { headers })
                    if (response.data.status === 0) {
                        context.commit('SET_MEMBER', response.data)
                    } else {
                        context.commit('UNSET_MEMBER')
                    }
                } catch (error) {
                    context.commit('UNSET_MEMBER')
                    return error.response
                }
                return response
            },
            async REFRESH_ACCESS_TOKEN (context) {
                if (typeof context.state.refresh_token_member !== 'string' || context.state.refresh_token_member.split('.').length !== 3) {
                    return { status: 401 }
                }

                const headers = {
                    'x-token': context.state.refresh_token_member
                }
                let response = {}
                try {
                    response = await axios.post(`${MEMBERS_PATH}/refresh_access_token`, { }, { headers })
                    context.commit('INIT_TOKEN_MEMBER', response.data)
                } catch (error) {
                    response = error.response
                }
                return response
            },
            async CONTACT (context, data) {
                let result = {}
                try {
                    result = await axios.post(`${USERS_PATH}/contact`, data)
                } catch (error) {
                    return error.response.data
                }
                return result.data
            }
        }
    })

    // const process_queue = (error, token = null) => {
    //     failed_queue.forEach(prom => {
    //         if (error) {
    //             prom.reject(error)
    //         } else {
    //             prom.resolve(token)
    //         }
    //     })

    //     failed_queue = []
    // }

    // axios.defaults.baseURL = WEBSITE_URL

    // Add a request interceptor
    axios.interceptors.request.use(function (config) {
        return new Promise((resolve, reject) => {
            if (config.url.endsWith(`${USERS_PATH}/lookup`) || config.url.endsWith(`${USERS_PATH}/login`)) {
                return resolve(config)
            }

            // get info and logout for members (different scopes)
            if (typeof config.headers['x-token'] === 'string') {
                // do nothing if the token was set in the ACTION
            // inventory location request new token
            } else if (typeof store.state.access_token_member === 'string' && store.state.access_token_member.split('.').length === 3) {
                config.headers['x-token'] = store.state.access_token_member
            }

            resolve(config)
        })
    //     let interval = null
    //     return new Promise((resolve, reject) => {
    //         if (config.url.endsWith(`${USERS_PATH}/register`) || config.url.endsWith(`${USERS_PATH}/login`)) {
    //             return resolve(config)
    //         }
    //         if (store.state.is_authorized && typeof store.state.token === 'string' && store.state.token.split('.').length === 3) {
    //             config.headers['x-token'] = store.state.token
    //             resolve(config)
    //         } else {
    //             interval = setInterval(() => {
    //                 if (store.state.is_authorized && typeof store.state.token === 'string' && store.state.token.split('.').length === 3) {
    //                     clearInterval(interval)
    //                     config.headers['x-token'] = store.state.token
    //                     resolve(config)
    //                 }
    //             }, 200)
    //         }
    //     })
    }, function (error) {
        return Promise.reject(error)
    })

    // Refresh once for concurrent 401 responses, then replay each original request.
    axios.interceptors.response.use(function (response) {
        return response
    }, async function (error) {
        const response = error.response
        const original_request = error.config
        const request_url = original_request && typeof original_request.url === 'string' ? original_request.url : ''
        const refresh_url = `${MEMBERS_PATH}/refresh_access_token`
        const logout_url = `${MEMBERS_PATH}/logout`
        const login_url = `${WWW_MEMBERS_PATH}/login`

        if (!response || response.status !== 401 || !original_request || original_request._member_refresh_retry || request_url.endsWith(refresh_url) || request_url.endsWith(logout_url) || request_url.endsWith(login_url)) {
            return Promise.reject(error)
        }

        const refresh_token = store.state.refresh_token_member
        if (typeof refresh_token !== 'string' || refresh_token.split('.').length !== 3) {
            store.commit('UNSET_MEMBER')
            store.commit('UNSET_TOKEN_MEMBER')
            return Promise.reject(error)
        }

        original_request._member_refresh_retry = true

        if (!refresh_request) {
            refresh_request = store.dispatch('REFRESH_ACCESS_TOKEN')
                .then(function (refresh_response) {
                    if (refresh_response && refresh_response.status === 200) {
                        return {
                            token: store.state.access_token_member,
                            terminal: false
                        }
                    }

                    const terminal = Boolean(refresh_response && [401, 403].includes(refresh_response.status))
                    if (terminal) {
                        store.commit('UNSET_MEMBER')
                        store.commit('UNSET_TOKEN_MEMBER')
                    }

                    return {
                        token: null,
                        terminal
                    }
                })
                .finally(function () {
                    refresh_request = null
                })
        }

        const refreshed = await refresh_request
        if (!refreshed.token) {
            // Let route guards distinguish an invalid session from a temporary
            // network/server refresh failure without discarding valid cookies.
            response.member_refresh_temporary_failure = !refreshed.terminal
            return Promise.reject(error)
        }

        if (original_request.headers && typeof original_request.headers.set === 'function') {
            original_request.headers.set('x-token', refreshed.token)
        } else {
            original_request.headers = original_request.headers || {}
            original_request.headers['x-token'] = refreshed.token
        }

        return axios(original_request)
    })

    return store
}
